/**
 * 74.13 step 4 (ARCHITECTURE §5.47: "they must never overlap a base's pads"): the landforms' records (`PlanetState.shapings`,
 * core/shapingGround.ts) cleared from under the structures setup placed — read by match/setup.ts once the bases are planned,
 * before the terraces and the decks read the ground. Off (no records), nothing here runs.
 */
import type { GameState } from '../types';
import { cellsOf } from './shapingGround';
import { structureFootprint } from './buildingScale';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { dcos } from './dmath';

/**
 * Setup (374's note: a landform's records never overlap a base's pads): drop the records that reach a structure's footprint
 * (core/buildingScale.ts structureFootprint) and a margin — the sites pass keeps bases on plains, and where one stands by a
 * rim or a crest the ground under it stays the mesh's. A new planet only where a record goes.
 */
export const SHAPING_CLEAR_PEOPLE = 3;
export function clearShapingsUnder(state: GameState): GameState {
  if (!state.planets.some(p => p.shapings?.length) || state.structures.length === 0) return state;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  let changed = false;
  const planets = state.planets.map((planet, pi) => {
    const list = planet.shapings;
    if (!list?.length) return planet;
    const c = cellsOf(list, planet.radius), R = planet.radius;
    const drop = new Uint8Array(list.length);
    for (const st of state.structures) {
      if (st.planetIndex !== pi) continue;
      const u = st.surfaceDir, ul = Math.sqrt(u.x * u.x + u.y * u.y + u.z * u.z) || 1;
      const foot = structureFootprint(st, state.config, R) + SHAPING_CLEAR_PEOPLE * person;
      for (let i = 0; i < list.length; i++) {
        if (drop[i]) continue;
        const cd = c.centres[i].dir, cos = (u.x * cd.x + u.y * cd.y + u.z * cd.z) / ul;
        if (cos >= dcos(Math.min(Math.PI, (c.extent[i] + foot) / R))) drop[i] = 1;
      }
    }
    if (!drop.some(x => x === 1)) return planet;
    changed = true;
    const kept = list.filter((_, i) => !drop[i]);
    return { ...planet, shapings: kept.length ? kept : undefined };
  });
  return changed ? { ...state, planets } : state;
}
